// src/components/Header.jsx
import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Avatar,
  Tooltip,
  useMediaQuery,
  Divider,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useThemeToggle } from "../context/ThemeContext";
import NotificationIcon from './NotificationIcon';
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";

const Header = ({ open, onToggle, user, pageTitle = "Dashboard" }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:800px)");
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const isVerySmallScreen = useMediaQuery("(max-width:400px)");
  const drawerWidth = open ? 240 : 68;

  const [profileAnchor, setProfileAnchor] = React.useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);

  // ✅ Convert backend role names to user-friendly display names
  const getDisplayRole = (role) => {
    switch (role) {
      case "superAdmin":
        return "Super Admin";
      case "admin":
        return "Admin";
      case "teamMember":
        return "Employee";
      default:
        return "User";
    }
  };

  // ✅ Check if user should see notifications based on role
  const shouldShowNotifications = () => {
    if (!user || !user.role) return false;
    const allowedRoles = ["superAdmin", "admin", "teamMember"];
    return allowedRoles.includes(user.role);
  };

  const handleProfileClick = (event) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchor(null);
  };

  const handleProfileNavigate = () => {
    navigate("/profile");
    handleProfileClose();
  };

  const handleSettingsNavigate = () => {
    navigate("/settings");
    handleProfileClose();
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    handleProfileClose();
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <>
      <AppBar
        elevation={0}
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: "all 0.3s ease-in-out",
          width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
          ml: isMobile ? 0 : `${drawerWidth}px`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            minHeight: { xs: 56, sm: 64, md: 72 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          {/* ---------- Left Section ---------- */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Always show menu icon on mobile */}
            {isMobile ? (
              <IconButton
                onClick={() => onToggle()}
                edge="start"
                sx={{
                  color: "primary.main",
                  "&:hover": { backgroundColor: "accent.light" },
                  padding: { xs: '6px', sm: '8px' }
                }}
              >
                <MenuIcon fontSize={isVerySmallScreen ? "small" : "medium"} />
              </IconButton>
            ) : (
              // Desktop menu button
              !open && (
                <IconButton
                  onClick={() => onToggle()}
                  edge="start"
                  sx={{
                    color: "primary.main",
                    "&:hover": { backgroundColor: "accent.light" },
                    padding: '8px'
                  }}
                >
                  <MenuIcon fontSize="medium" />
                </IconButton>
              )
            )}
            
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: { 
                  xs: "0.9rem", 
                  sm: "1.1rem", 
                  md: "1.25rem" 
                },
                letterSpacing: 0.4,
                maxWidth: { 
                  xs: "100px", 
                  sm: "150px", 
                  md: "none" 
                },
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {pageTitle}
            </Typography>
          </Box>

          {/* ---------- Right Section ---------- */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1, md: 2 } }}>
            
            {/* ✅ Notification Bell Component - Role-based visibility */}
            {shouldShowNotifications() && user?._id && (
              <NotificationIcon 
                userId={user._id} 
                sx={{
                  color: "primary.main",
                  border: isVerySmallScreen ? 'none' : `1.5px solid`,
                  borderColor: "primary.main",
                  backgroundColor: isVerySmallScreen ? 'transparent' : "background.default",
                  "&:hover": {
                    backgroundColor: "accent.light",
                  },
                }}
              />
            )}

            {/* Divider - Hide on very small screens */}
            {!isVerySmallScreen && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mx: { xs: 0.5, sm: 1 },
                  borderColor: "primary.main",
                  opacity: 0.3,
                }}
              />
            )}

            {/* ---------- User Info Section ---------- */}
            {user && (
              <>
                <Tooltip 
                  title={isVerySmallScreen ? `${user.name || "User"} - ${getDisplayRole(user.role)}` : ""}
                  placement="bottom"
                >
                  <Box
                    onClick={handleProfileClick}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 1, md: 1.5 },
                      px: { xs: 1, sm: 1.5, md: 2 },
                      py: { xs: 0.5, sm: 0.6, md: 0.8 },
                      borderRadius: 3,
                      backgroundColor: "background.default",
                      border: `1.5px solid`,
                      borderColor: "secondary.main",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "accent.light",
                        boxShadow: "0 0 10px rgba(0,0,0,0.06)",
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <Avatar
                      src={user.avatar || ""}
                      alt={user.name || "User"}
                      sx={{
                        width: { xs: 32, sm: 36, md: 42 },
                        height: { xs: 32, sm: 36, md: 42 },
                        bgcolor: "primary.main",
                        color: mode === "light" ? "#fff" : "#0f172a",
                        fontWeight: 600,
                        fontSize: { xs: "0.8rem", sm: "0.9rem", md: "0.95rem" },
                      }}
                    >
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </Avatar>

                    {/* User details - Hide on very small screens, show on larger screens */}
                    {!isVerySmallScreen && (
                      <Box sx={{ lineHeight: 1.2, display: { xs: 'none', sm: 'block' } }}>
                        <Typography
                          variant="subtitle1"
                          noWrap
                          sx={{
                            fontWeight: 600,
                            color: "primary.main",
                            fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.95rem" },
                            maxWidth: { xs: "80px", sm: "100px", md: "120px" },
                          }}
                        >
                          {user.name || "User"}
                        </Typography>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            color: "secondary.main",
                            fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                            textTransform: "capitalize",
                            maxWidth: { xs: "80px", sm: "100px", md: "120px" },
                          }}
                        >
                          {getDisplayRole(user.role)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Tooltip>

                {/* Profile Dropdown Menu */}
                <Menu
                  anchorEl={profileAnchor}
                  open={Boolean(profileAnchor)}
                  onClose={handleProfileClose}
                  onClick={handleProfileClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                >
                  <MenuItem onClick={handleProfileNavigate}>
                    <ListItemIcon>
                      <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Profile</ListItemText>
                  </MenuItem>
                  
                  <MenuItem onClick={handleSettingsNavigate}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Settings</ListItemText>
                  </MenuItem>
                  
                  <MenuItem onClick={handleLogoutClick} sx={{ color: 'error.main' }}>
                    <ListItemIcon sx={{ color: 'error.main' }}>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: mode === "dark" 
              ? "linear-gradient(135deg, #1f2937 0%, #374151 100%)" 
              : "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            border: mode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)",
          }
        }}
      >
        <Box sx={{ textAlign: "center", py: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor: "rgba(239,68,68,0.1)",
              margin: "0 auto 16px",
              border: "2px solid rgba(239,68,68,0.2)",
            }}
          >
            <LogoutIcon 
              sx={{ 
                fontSize: "1.75rem", 
                color: "#ef4444" 
              }} 
            />
          </Box>
          
          <DialogTitle 
            sx={{ 
              pb: 1,
              fontSize: "1.5rem",
              fontWeight: 600,
              color: mode === "dark" ? "white" : "text.primary",
            }}
          >
            Confirm Logout
          </DialogTitle>
          
          <DialogContent sx={{ py: 1 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: mode === "dark" ? "grey.300" : "grey.600",
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to logout from your account?
            </Typography>
          </DialogContent>
        </Box>
        
        <DialogActions 
          sx={{ 
            px: 3, 
            pb: 3, 
            gap: 2,
            justifyContent: "center",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleLogoutCancel}
            sx={{ 
              textTransform: "none",
              px: 4,
              py: 1,
              borderRadius: 2,
              borderWidth: "2px",
              fontWeight: 500,
              "&:hover": {
                borderWidth: "2px",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleLogoutConfirm}
            startIcon={<LogoutIcon />}
            sx={{ 
              textTransform: "none",
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 500,
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              boxShadow: "0 4px 12px rgba(239,68,68,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                boxShadow: "0 6px 16px rgba(239,68,68,0.4)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;